import React, { useEffect, useMemo, useState } from 'react';
import { HiCheckCircle } from 'react-icons/hi';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useJwt } from '../../helpers/common';
import { useOrganizationService } from '../../services';
import useIntegrationStatus from '../../hooks/useIntegrationStatus';
import { OnboardingStepKeyEnum, PMTOOLS_PROVIDER_ORDER, useOnboardingNavigator } from '../../components/Onboarding';
import { DashboardStatus } from '../../constants/integrationTypes';
import { setOrganizationOnboardSettings } from '../../redux/slices/organization';
import { motion, AnimatePresence } from "framer-motion";

const OnboardingPage = () => {
    const jwtData: any = useJwt();
    const [isInitialized, setIsInitialized] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { updateOrganization, fetchOrgIntegrations, fetchOrganization } = useOrganizationService();
    const { onboardSettings = {} } = useSelector((state: any) => state.organization);
    const { roadmaps = [] } = useSelector((state: any) => state.roadmap || {});

    const { isIntegrationActive } = useIntegrationStatus();
    const { goToStep } = useOnboardingNavigator();

    useEffect(() => {
        fetchOrgIntegrations();
    }, []);

    useEffect(() => {
        const initOrg = async () => {
            if (jwtData?.org_id) {
                try {
                    await fetchOrganization(jwtData.org_id);
                    // Minimal delay to show the nice loader animation
                    setTimeout(() => setIsInitialized(true), 800);
                } catch (error) {
                    console.error("Error fetching organization:", error);
                    setIsInitialized(true);
                }
            }
        };
        initOrg();
    }, [jwtData?.org_id]);

    const handleClickableLink = async (key?: OnboardingStepKeyEnum) => {
        if (!jwtData?.org_id || !isInitialized) return;

        let next = onboardSettings || {};
        if (key) {
            // Logic mirrored from TourGuide/index.tsx
            if (onboardSettings?.dismissed === true) {
                next = { ...next, dismissed: false, current_navigation: key };
                await updateOrganization(jwtData.org_id, { onboard_settings: next }, true);
            }
            
            try { window.dispatchEvent(new CustomEvent('onboarding:start', { detail: { key } })); } catch { }

            next = { ...next, current_navigation: key };
            dispatch(setOrganizationOnboardSettings(next));
            goToStep(key);
            return;
        }
    };

    type StepItem = {
        id: number;
        label: string;
        description: string;
        done: boolean;
        onClick?: () => void;
        key?: string;
    };

    const hasAtRiskRoadmaps = useMemo(() => Array.isArray(roadmaps) && roadmaps.some((r: any) => String(r?.status || '').toLowerCase() === DashboardStatus.AT_RISK), [roadmaps]);

    const steps: StepItem[] = useMemo(() => {
        const azureActive = isIntegrationActive('azure');
        const jiraActive = isIntegrationActive('jira');

        const pmDone = PMTOOLS_PROVIDER_ORDER.every(k => onboardSettings?.pm_tools?.[k]);

        const stepsList: StepItem[] = [
            {
                id: 1,
                label: "Create An Org",
                done: true,
                onClick: undefined,
                description: "Your workspace is ready"
            },
        ];

        if (hasAtRiskRoadmaps) {
            stepsList.push({
                id: stepsList.length + 1,
                label: "Risk Mitigation",
                done: !!(onboardSettings?.risk_mitigation?.awareness && onboardSettings?.risk_mitigation?.analyze),
                onClick: () => handleClickableLink(OnboardingStepKeyEnum.RiskMitigation),
                description: "Identify and mitigate risks"
            });
        }

        stepsList.push({
            id: stepsList.length + 1,
            label: "Integrate PM Tools",
            done: pmDone,
            onClick: () => handleClickableLink(OnboardingStepKeyEnum.PmTools),
            description: "Connect Jira / Azure / more"
        });

        let nextId = stepsList.length + 1;

        if (azureActive || jiraActive) {
            stepsList.push({
                id: nextId++,
                label: jiraActive ? "Import Users From Jira" : "Import Users From Azure DevOps",
                done: !!onboardSettings.import_users,
                onClick: () => handleClickableLink(OnboardingStepKeyEnum.ImportUsers),
                description: "Import users from your PM Tool"
            });
        }

        if (azureActive) {
            stepsList.push({
                id: nextId++,
                label: "Import Teams From Azure DevOps",
                done: !!onboardSettings.import_teams,
                onClick: () => handleClickableLink(OnboardingStepKeyEnum.ImportTeams),
                description: "Import teams from your PM Tool"
            });
        }

        stepsList.push(
            {
                id: nextId++,
                label: "Add Custom Skill",
                done: !!onboardSettings.org_skills,
                onClick: () => handleClickableLink(OnboardingStepKeyEnum.OrgSkills),
                description: "Manage skills in your organization"
            },
            {
                id: nextId++,
                label: "Allocate Skills",
                done: !!onboardSettings.allocate_skills,
                onClick: () => handleClickableLink(OnboardingStepKeyEnum.AllocateSkills),
                description: "Allocate skills to resources"
            },
            {
                id: nextId++,
                label: "Add Custom Holiday",
                done: !!onboardSettings.org_holidays,
                onClick: () => handleClickableLink(OnboardingStepKeyEnum.OrgHolidays),
                description: "Manage holidays of your organization"
            }
        );

        return stepsList;
    }, [isIntegrationActive, onboardSettings, hasAtRiskRoadmaps]);

    const progress = useMemo(() => {
        const completed = steps.filter(s => s.done).length;
        return Math.round((completed / steps.length) * 100);
    }, [steps]);

    const firstIncompleteIndex = useMemo(() => {
        return steps.findIndex(s => !s.done);
    }, [steps]);


    return (
        <div className="min-h-screen bg-white dark:bg-[#0F1115] flex flex-col w-full items-center justify-center relative overflow-hidden font-sans transition-colors duration-300 py-[100px] overflow-y-scroll">
             {/* Background Effects */}
             <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-blue-500/10 dark:bg-blue-500/10 rounded-full blur-[120px] pointer-events-none" />
             <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-indigo-500/10 dark:bg-indigo-500/10 rounded-full blur-[120px] pointer-events-none" />

            <AnimatePresence mode="wait">
                {!isInitialized ? (
                    <motion.div 
                        key="loader"
                        className="z-20 flex flex-col items-center justify-center"
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.5 }}
                    >
                         <motion.img
                            src="https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/cadencelogo.webp"
                            className="h-24 w-24 object-contain"
                            alt="Loading..."
                            animate={{ rotate: 360 }}
                            transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                        />
                    </motion.div>
                ) : (
                    <motion.div 
                        key="content"
                        className="z-10 w-full max-w-4xl px-6 flex flex-col items-center"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                    >
                        {/* Header / Logo */}
                        <div className="mb-8 flex flex-col items-center">
                            <motion.img
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.2, duration: 0.5 }}
                                src="https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/cadencelogo.webp"
                                className="h-24 w-24 mb-4 object-contain drop-shadow-[0_0_15px_rgba(59,130,246,0.5)]"
                                alt="Cadence Pro"
                            />
                            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white tracking-tight">Cadence Pro</h1>
                        </div>

                        {/* Welcome & Progress */}
                        <div className="w-full text-center mb-12">
                            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">Welcome to Cadence Pro</h2>
                            <div className="flex flex-col items-center gap-4">
                                <p className="text-gray-500 dark:text-gray-400 text-lg">Lets get you set up. {progress}% completed</p>
                                
                                <div className="w-full max-w-md h-3 bg-gray-200 dark:bg-gray-800/50 rounded-full overflow-hidden backdrop-blur-sm border border-gray-300 dark:border-white/5 relative box-border">
                                    <div 
                                        className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all duration-1000 ease-out shadow-[0_0_10px_rgba(59,130,246,0.5)]"
                                        style={{ width: `${progress}%` }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Steps Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                            {steps.map((step, idx) => {
                                const isCurrent = idx === firstIncompleteIndex;
                                const isDone = step.done;
                                
                                return (
                                    <motion.div 
                                        key={step.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.3 + (idx * 0.1) }}
                                        className={`
                                            group relative p-6 rounded-2xl border transition-all duration-300
                                            flex items-center gap-4 cursor-pointer
                                            ${isDone 
                                                ? 'bg-gray-100/50 dark:bg-gray-900/40 border-gray-200 dark:border-gray-800/50 text-gray-400 dark:text-gray-400 hover:bg-gray-200/50 dark:hover:bg-gray-800/40' 
                                                : isCurrent
                                                    ? 'bg-white dark:bg-gradient-to-b dark:from-gray-800/80 dark:to-gray-900/80 border-blue-500/50 shadow-[0_0_20px_rgba(59,130,246,0.15)] hover:border-blue-400/70 hover:shadow-[0_0_25px_rgba(59,130,246,0.25)]'
                                                    : 'bg-white/50 dark:bg-gray-900/60 border-gray-200 dark:border-gray-800/50 text-gray-600 dark:text-gray-300 hover:bg-gray-100/50 dark:hover:bg-gray-800/60'
                                            }
                                            backdrop-blur-md
                                        `}
                                        onClick={() => !isDone && step.onClick?.()}
                                    >
                                        {/* Accessory Icon / Number */}
                                        <div className="flex-shrink-0">
                                            {isDone ? (
                                                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center text-green-600 dark:text-green-400 border border-green-500/30">
                                                    <HiCheckCircle className="w-6 h-6" />
                                                </div>
                                            ) : isCurrent ? (
                                                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-600 dark:text-blue-400 border border-blue-500/30 shadow-[0_0_10px_rgba(59,130,246,0.3)]">
                                                    <OnboardingIconByType label={step.label} />
                                                </div>
                                            ) : (
                                                <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-800 flex items-center justify-center text-gray-500 border border-gray-300 dark:border-gray-700">
                                                    <span className="font-semibold">{idx + 1}</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className={`font-semibold text-lg mb-1 ${isDone ? 'line-through opacity-70' : 'text-gray-900 dark:text-white'}`}>
                                                {step.label}
                                            </div>
                                            <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                                {step.description}
                                            </div>
                                        </div>

                                        {/* Action Button (Only for current/incomplete) */}
                                        {(!isDone) && (
                                            <div className="flex-shrink-0">
                                                <button 
                                                    className={`
                                                        px-4 py-1.5 rounded-lg text-sm font-semibold transition-all
                                                        ${isCurrent
                                                            ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-900/20'
                                                            : 'bg-gray-200 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
                                                        }
                                                    `}
                                                    disabled={!isCurrent}
                                                >
                                                    Start
                                                </button>
                                            </div>
                                        )}
                                    </motion.div>
                                );
                            })}
                        </div>

                        {/* Footer */}
                        <div className="mt-16 text-center">
                            <button className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors text-sm flex items-center gap-2">
                                Need help? Contact Support
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
            
            {/* Bottom Right Decorative Star (from design) */}
            <div className="absolute bottom-8 right-8 text-gray-400 dark:text-gray-700 opacity-50">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z" />
                </svg>
            </div>
        </div>
    );
};

// Helper for icons based on step label/type to match design vibes
const OnboardingIconByType = ({ label }: { label: string }) => {
    // Add logic to return specific icons
    if (label.includes("Risk")) return (
         <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
    );
    if (label.includes("Integrate")) return (
         <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
    );
    if (label.includes("Custom Skill") || label.includes("Allocate")) return (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
    );
     if (label.includes("Holiday")) return (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
    );
    
    return (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
    );
}

export default OnboardingPage;
